import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { api } from "../../api/api";
import { tr } from "../../i18n";
import { DayTimeline, StatusBadge } from "./StatsParts";
import {
  formatAgo,
  formatDateTime,
  formatDuration,
  formatNumber,
  formatPct,
} from "./statsFormat";

function InfoRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="stats-info-row">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function memory(m) {
  if (!m || (!m.total && !m.free)) return "";
  const total = m.total && m.total !== "—" ? m.total : "";
  const free = m.free && m.free !== "—" ? m.free : "";
  if (!total && !free) return "";
  if (total && free) return tr("{free} free of {total}", { free, total });
  return total || free;
}

/** Side panel with one screen's timeline, device info and problems. */
export default function ScreenDetail({ contextUserId, screenId, view, onClose }) {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!screenId || !contextUserId) return;
    let active = true;
    setData(null);
    setError("");
    api
      .get(`/kpi/screen/${contextUserId}/${screenId}`, { params: { days, view } })
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => { if (active) setError(e?.response?.data?.message || tr("Could not load screen statistics.")); });
    return () => { active = false; };
  }, [contextUserId, screenId, days, view]);

  const s = data?.screen;
  const problems = (data?.events || []).filter((e) => e.status === "self_heal" || e.status === "load_error");

  return (
    <Drawer anchor="right" open={Boolean(screenId)} onClose={onClose} PaperProps={{ className: "stats-drawer" }}>
      <div className="stats-drawer-head">
        <div>
          <h2>{s?.name || tr("Screen")}</h2>
          {s && (
            <div className="stats-drawer-sub">
              <StatusBadge kind={s.online ? "online" : s.lastActive ? "offline" : "never"} />
              <span>{s.online ? tr("Online now") : tr("Last seen {when}", { when: formatAgo(s.lastActive, data.generatedAt) })}</span>
            </div>
          )}
        </div>
        <IconButton onClick={onClose} aria-label={tr("Close")}><CloseIcon /></IconButton>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && <Box sx={{ p: 4, textAlign: "center" }}><CircularProgress size={28} /></Box>}

      {data && (
        <div className="stats-drawer-body">
          <div className="stats-drawer-toolbar">
            <ToggleButtonGroup size="small" exclusive value={days} onChange={(e, v) => v && setDays(v)} aria-label={tr("Period")}>
              {[7, 14, 30].map((d) => (
                <ToggleButton key={d} value={d}>{tr("{n} days", { n: d })}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </div>

          <div className="stats-mini-tiles">
            <div>
              <span>{tr("Uptime in opening hours")}</span>
              <strong>{formatPct(data.uptime.window.pct)}</strong>
            </div>
            <div>
              <span>{tr("Uptime all day")}</span>
              <strong>{formatPct(data.uptime.allDay.pct)}</strong>
            </div>
            <div>
              <span>{tr("Outages")}</span>
              <strong>{formatNumber(data.uptime.window.incidents)}</strong>
            </div>
            <div>
              <span>{tr("Longest outage")}</span>
              <strong>{data.uptime.window.longestOutageMs ? formatDuration(data.uptime.window.longestOutageMs) : "–"}</strong>
            </div>
          </div>

          <section className="stats-section">
            <h3>{tr("Online and offline")}</h3>
            <DayTimeline segments={data.segments} from={data.from} now={data.generatedAt} window={data.window} />
          </section>

          <section className="stats-section">
            <h3>{tr("Device")}</h3>
            <div className="stats-info">
              {data.internal && <InfoRow label={tr("Customer")} value={s.customer} />}
              <InfoRow label={tr("Location")} value={s.location} />
              <InfoRow label={tr("Playlist")} value={s.playlist} />
              <InfoRow label={tr("Software")} value={s.software} />
              <InfoRow label={tr("Device")} value={[s.manufacturer, s.deviceName].filter(Boolean).join(" ")} />
              <InfoRow label={tr("OS version")} value={s.osVersion} />
              <InfoRow label={tr("Resolution")} value={s.resolution} />
              <InfoRow label={tr("RAM")} value={memory(s.ram)} />
              <InfoRow label={tr("Storage")} value={memory(s.storage)} />
              <InfoRow label={tr("Last seen")} value={formatDateTime(s.lastActive)} />
              <InfoRow label={tr("Added")} value={formatDateTime(s.createdAt)} />
              {data.internal && <InfoRow label={tr("MAC / device id")} value={s.mac} />}
              <InfoRow label={tr("Code")} value={s.code} />
            </div>
          </section>

          {data.internal && (
            <section className="stats-section">
              <h3>{tr("Player problems")} <span className="stats-internal-tag">{tr("Internal")}</span></h3>
              {problems.length === 0 ? (
                <p className="stats-muted">{tr("No self-heals or load errors in this period.")}</p>
              ) : (
                <Table size="small">
                  <TableBody>
                    {problems.slice(0, 50).map((e) => (
                      <TableRow key={`${e.status}-${e.at}`}>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>{formatDateTime(e.at)}</TableCell>
                        <TableCell><StatusBadge kind={e.status} /></TableCell>
                        <TableCell className="stats-detail-cell">{e.detail}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>
          )}
        </div>
      )}
    </Drawer>
  );
}
